import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus } from "lucide-react";

export default function Assignments() {
  const { user } = useAuth();
  const isStaff = user?.role === "teacher" || user?.role === "admin";
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [a, c] = await Promise.all([api.get("/assignments"), api.get("/courses")]);
    setAssignments(a.data); setCourses(c.data); setLoading(false);
  };
  useEffect(() => { load().catch(() => setLoading(false)); }, []);

  if (loading) return <p className="font-mono text-clay text-sm">加载中…</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Assignments</h1>
          <p className="text-clay mt-1">{isStaff ? "Create tasks and grade student work." : "Submit your work and view feedback."}</p>
        </div>
        {isStaff && <CreateAssignment courses={courses} onCreated={load} />}
      </div>

      {assignments.length === 0 ? (
        <div className="border border-black/10 bg-white p-12 text-center text-clay">No assignments yet.</div>
      ) : (
        <div className="space-y-4">
          {assignments.map((a) => (
            <div key={a.assignment_id} className="border border-black/10 bg-white p-6" data-testid={`assignment-${a.assignment_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="label text-gold">{courses.find((c) => c.course_id === a.course_id)?.title || "Course"}</p>
                  <h3 className="font-display font-bold text-xl mt-1">{a.title}</h3>
                  <p className="text-sm text-clay mt-1">{a.description}</p>
                  <p className="text-xs text-clay mt-2 font-mono">Max score: {a.max_score}{a.due_date ? ` · Due ${a.due_date}` : ""}</p>
                </div>
              </div>
              {isStaff
                ? <TeacherRow assignment={a} onGraded={load} />
                : <StudentRow assignment={a} onSubmitted={load} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateAssignment({ courses, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ course_id: "", title: "", description: "", due_date: "", max_score: 100 });
  const submit = async () => {
    if (!form.course_id || !form.title) { toast.error("Course and title are required"); return; }
    try {
      await api.post("/assignments", { ...form, max_score: Number(form.max_score) });
      toast.success("Assignment created");
      setOpen(false); setForm({ course_id: "", title: "", description: "", due_date: "", max_score: 100 });
      onCreated();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-assignment-btn" className="inline-flex items-center gap-2 px-5 py-3 bg-ink text-rice text-sm hover:bg-cinnabar transition-colors"><Plus size={16} /> New Assignment</button>
      </DialogTrigger>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Create Assignment</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="label text-clay">Course</label>
            <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} data-testid="assign-course"
              className="w-full mt-1.5 px-4 py-2.5 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar">
              <option value="">Select course…</option>
              {courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
            </select>
          </div>
          <div><label className="label text-clay">Title</label>
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="assign-title"
              className="w-full mt-1.5 px-4 py-2.5 border border-black/10 focus:outline-none focus:ring-1 focus:ring-cinnabar" /></div>
          <div><label className="label text-clay">Description</label>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="assign-desc"
              className="w-full mt-1.5 px-4 py-2.5 border border-black/10 focus:outline-none focus:ring-1 focus:ring-cinnabar" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label text-clay">Due date</label>
              <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="assign-due"
                className="w-full mt-1.5 px-4 py-2.5 border border-black/10 focus:outline-none focus:ring-1 focus:ring-cinnabar" /></div>
            <div><label className="label text-clay">Max score</label>
              <input type="number" value={form.max_score} onChange={(e) => setForm({ ...form, max_score: e.target.value })} data-testid="assign-score"
                className="w-full mt-1.5 px-4 py-2.5 border border-black/10 focus:outline-none focus:ring-1 focus:ring-cinnabar" /></div>
          </div>
        </div>
        <DialogFooter><button onClick={submit} data-testid="assign-save" className="px-6 py-3 bg-ink text-rice hover:bg-cinnabar transition-colors">Create</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StudentRow({ assignment, onSubmitted }) {
  const [content, setContent] = useState(assignment.my_submission?.content || "");
  const [busy, setBusy] = useState(false);
  const ev = assignment.my_evaluation;
  const submit = async () => {
    if (!content.trim()) { toast.error("Write your answer first"); return; }
    setBusy(true);
    try {
      await api.post("/submissions", { assignment_id: assignment.assignment_id, content });
      toast.success("Submitted!"); onSubmitted();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-4 border-t border-black/10 pt-4">
      {ev ? (
        <div className="bg-cinnabar/5 border border-cinnabar/20 p-4">
          <p className="label text-cinnabar">Graded · {ev.score}/{assignment.max_score}</p>
          <p className="text-sm text-ink mt-2"><strong>Feedback:</strong> {ev.feedback}</p>
          <p className="text-xs text-clay mt-1">by {ev.evaluated_by}</p>
        </div>
      ) : (
        <>
          <textarea rows={3} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Type your submission…"
            data-testid={`submit-content-${assignment.assignment_id}`}
            className="w-full px-4 py-2.5 border border-black/10 focus:outline-none focus:ring-1 focus:ring-cinnabar text-sm" />
          <div className="flex items-center gap-3 mt-3">
            <button onClick={submit} disabled={busy} data-testid={`submit-btn-${assignment.assignment_id}`}
              className="px-5 py-2.5 bg-ink text-rice text-sm hover:bg-cinnabar transition-colors disabled:opacity-60">
              {assignment.my_submission ? "Update Submission" : "Submit"}
            </button>
            {assignment.my_submission && <span className="text-xs text-clay">Submitted · awaiting grade</span>}
          </div>
        </>
      )}
    </div>
  );
}

function TeacherRow({ assignment, onGraded }) {
  const [open, setOpen] = useState(false);
  const [subs, setSubs] = useState([]);
  const load = async () => { const { data } = await api.get(`/assignments/${assignment.assignment_id}/submissions`); setSubs(data); };
  const toggle = async () => { const n = !open; setOpen(n); if (n) await load(); };
  return (
    <div className="mt-4 border-t border-black/10 pt-4">
      <button onClick={toggle} data-testid={`view-subs-${assignment.assignment_id}`}
        className="text-sm font-medium text-cinnabar hover:underline">
        {open ? "Hide" : "View"} submissions ({assignment.submission_count || 0})
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          {subs.length === 0 && <p className="text-sm text-clay">No submissions yet.</p>}
          {subs.map((s) => <GradeRow key={s.submission_id} sub={s} maxScore={assignment.max_score} onGraded={() => { load(); onGraded(); }} />)}
        </div>
      )}
    </div>
  );
}

function GradeRow({ sub, maxScore, onGraded }) {
  const [score, setScore] = useState(sub.evaluation?.score ?? "");
  const [feedback, setFeedback] = useState(sub.evaluation?.feedback ?? "");
  const save = async () => {
    if (score === "") { toast.error("Enter a score"); return; }
    try {
      await api.post("/evaluations", { submission_id: sub.submission_id, score: Number(score), feedback });
      toast.success("Graded"); onGraded();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <div className="border border-black/10 p-4 bg-rice" data-testid={`grade-${sub.submission_id}`}>
      <p className="font-display font-semibold text-sm">{sub.student_name}</p>
      <p className="text-sm text-clay mt-1 whitespace-pre-wrap">{sub.content}</p>
      <div className="flex flex-wrap items-end gap-3 mt-3">
        <div><label className="label text-clay">Score /{maxScore}</label>
          <input type="number" value={score} onChange={(e) => setScore(e.target.value)} data-testid={`score-${sub.submission_id}`}
            className="w-24 mt-1 px-3 py-2 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar text-sm" /></div>
        <div className="flex-1 min-w-[180px]"><label className="label text-clay">Feedback</label>
          <input value={feedback} onChange={(e) => setFeedback(e.target.value)} data-testid={`feedback-${sub.submission_id}`}
            className="w-full mt-1 px-3 py-2 border border-black/10 bg-white focus:outline-none focus:ring-1 focus:ring-cinnabar text-sm" /></div>
        <button onClick={save} data-testid={`grade-save-${sub.submission_id}`}
          className="px-4 py-2 bg-cinnabar text-rice text-sm hover:bg-ink transition-colors">{sub.evaluation ? "Update" : "Grade"}</button>
      </div>
    </div>
  );
}
